import { formatPrice } from '@/lib/utils'
import React from 'react'
import {
  Br,
  Cut,
  Line,
  Printer,
  Row,
  Text,
  render,
} from 'react-thermal-printer'

interface ReturnOrderData {
  order_return_items?: any[]
  order_return?: any
  'gift-card'?: any
}

interface PrinterEndpoint {
  direction: string
  endpointNumber: number
}

interface USBPrinter extends USBDevice {
  configuration: USBConfiguration & {
    interfaces: {
      alternate: {
        endpoints: PrinterEndpoint[]
      }
    }[]
  }
}

export const useReturnOrderPrinter = () => {
  const [printer, setPrinter] = React.useState<USBPrinter | null>(null)

  const requestPrinter = async (): Promise<USBPrinter | null> => {
    try {
      const device = (await navigator.usb.requestDevice({
        filters: [
          {
            vendorId: 0x0456, // Vendor ID for the printer
          },
        ],
      })) as USBPrinter
      setPrinter(device)
      return device
    } catch (e) {
      console.error('Error connecting to device:', e)
      return null
    }
  }

  const getPrinter = async (): Promise<USBPrinter | null> => {
    try {
      const devices = await navigator.usb.getDevices()
      if (devices.length > 0) {
        const device = devices[0] as USBPrinter
        setPrinter(device)
        return device
      }
      return null
    } catch (e) {
      console.error('Error getting devices:', e)
      return null
    }
  }

  const template = (data: ReturnOrderData) => {
    const { order_return_items, order_return } = data
    const giftCard = data['gift-card']

    // Early return if required data is missing
    if (!order_return_items || !order_return) return null

    return (
      <Printer type="star" width={48}>
        <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
          Return Receipt
        </Text>

        <Line />

        <Row
          left={<Text>Order ID</Text>}
          right={<Text>#{order_return?.order_id}</Text>}
        />
        <Row
          left={<Text>Returned on</Text>}
          right={<Text>{order_return?.created_at}</Text>}
        />

        <Line />

        <Text bold={true}>Returned Items</Text>

        <Line />

        {Array.isArray(order_return_items) &&
          order_return_items.map((item, index) => (
            <React.Fragment key={`item-${index}`}>
              <Row
                left={<Text>Item ID: {item.order_item_id}</Text>}
                right={<Text bold={true}>x{item?.returned_qty}</Text>}
                gap={5}
              />
              <Row
                left={<Text>Amount</Text>}
                right={
                  <Text bold={true}>
                    {formatPrice(Number(item?.return_amount), 2, false)}
                  </Text>
                }
                gap={5}
              />
              <Line />
            </React.Fragment>
          ))}

        <Row
          left={<Text bold={true}>Total Return Amount</Text>}
          right={
            <Text bold={true} size={{ width: 1, height: 2 }}>
              {formatPrice(Number(order_return?.total_return_amount), 2, false)}
            </Text>
          }
          gap={5}
        />

        <Line />

        {giftCard && (
          <>
            <Text bold={true}>Gift Card</Text>
            <Line />
            <Row
              left={<Text>Barcode</Text>}
              right={<Text>{giftCard?.barcode || 'N/A'}</Text>}
              gap={5}
            />
            <Row
              left={<Text>Amount</Text>}
              right={
                <Text>{formatPrice(Number(giftCard?.amount), 2, false)}</Text>
              }
              gap={5}
            />
            <Line />
          </>
        )}

        <Br />
        <Text align="center">System by et.lk</Text>
        <Cut />
      </Printer>
    )
  }

  const printReceipt = async (data: ReturnOrderData): Promise<void> => {
    try {
      let currentPrinter = await getPrinter()

      if (!currentPrinter) {
        currentPrinter = await requestPrinter()
        if (!currentPrinter) {
          throw new Error('No printer available')
        }
      }

      // Render template in a separate operation
      const printData: Uint8Array = await new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            const templateResult = template(data)
            if (!templateResult) {
              reject(new Error('Failed to generate template'))
              return
            }
            const rendered = await render(templateResult)
            resolve(rendered)
          } catch (error) {
            reject(error)
          }
        }, 0)
      })

      await currentPrinter.open()
      await currentPrinter.selectConfiguration(1)
      await currentPrinter.claimInterface(0)

      const endpoint =
        currentPrinter.configuration.interfaces[0].alternate.endpoints.find(
          (ep: PrinterEndpoint) => ep.direction === 'out',
        )

      if (!endpoint) {
        throw new Error('No valid endpoint found')
      }

      await currentPrinter.transferOut(endpoint.endpointNumber, printData)
      await currentPrinter.close()
    } catch (e) {
      console.error('Error printing receipt:', e)
      throw e
    }
  }

  return {
    printer,
    requestPrinter,
    getPrinter,
    template,
    printReceipt,
  }
}

